import React from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { SafetyPlanStatus } from '@lyrahealth-inc/shared-app-logic/src/models/safetyPlan/SafetyPlan'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SafetyPlanIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SafetyPlanIcon'
import { Link } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/link/Link'
import { Title, Size as TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { useClientSafetyPlan } from '../../../lyraTherapy/safetyPlan/data/useClientSafetyPlan'

const Row = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing['8px'],
}))

const Details = styled.View(({ theme }) => ({
  flex: 1,
  gap: theme.spacing['4px'],
}))

const TitleRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['8px'],
}))

const SafetyPlanSection: React.FC<{ clientId: string; onPress: () => void }> = ({ clientId, onPress }) => {
  const { colors } = useTheme()
  const intl = useIntl()
  const { data: safetyPlan } = useClientSafetyPlan(clientId)

  const isDraft = safetyPlan?.status === SafetyPlanStatus.DRAFT
  const formatPlanDate = (date: string) =>
    intl.formatDate(new Date(date), { month: 'short', day: 'numeric', year: 'numeric' })
  const details = !safetyPlan
    ? intl.formatMessage({
        defaultMessage: 'No plan created',
        description: 'Shown in the risk alert popover when the client has no safety plan',
      })
    : isDraft
      ? intl.formatMessage(
          { defaultMessage: 'Draft · Auto-saved {date}', description: 'The client safety plan is an unshared draft' },
          { date: formatPlanDate(safetyPlan.update_date) },
        )
      : intl.formatMessage(
          { defaultMessage: 'Created {date}', description: 'When the client safety plan was created' },
          { date: formatPlanDate(safetyPlan.create_date) },
        )

  return (
    <Row testID={tID('RiskAlertPopover-safety-plan')}>
      <SafetyPlanIcon size={24} fillColor={colors.iconDefault} />
      <Details>
        <TitleRow>
          <Title
            size={TitleSize.XSMALL}
            color={colors.textSecondary}
            text={intl.formatMessage({ defaultMessage: 'Safety plan', description: 'Safety plan section title' })}
          />
          <Link
            text={
              safetyPlan
                ? intl.formatMessage({ defaultMessage: 'Edit plan', description: 'Link to edit the safety plan' })
                : intl.formatMessage({ defaultMessage: 'Add plan', description: 'Link to create a safety plan' })
            }
            onPress={onPress}
            size={BodyTextSize.SMALL}
            underline
            testID={tID('RiskAlertPopover-safety-plan-link')}
          />
        </TitleRow>
        <BodyText
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          text={details}
          testID={tID('RiskAlertPopover-safety-plan-details')}
        />
      </Details>
    </Row>
  )
}

export default SafetyPlanSection
